import { buildQuoteConfiguration } from '../quote/quotePayload.js'
import { resolveQuoteEndpoint } from '../quote/quoteEndpoint.js'
import { collectScreenshots } from '../quote/shotSpec.js'
import { buildViewUrl } from '../config/configCodec.js'

// ============================================================================
//  quoteModal — modal „Zapytaj o wycenę". Jeden modal, dwa stany przełączane
//  bez przeładowania: (A) formularz → po sukcesie (B) podziękowanie. Stopka
//  kontaktowa widoczna w obu stanach. Buduje DOM raz, dołącza do document.body.
//
//  state/catalog/hoopCatalog trzymamy przez referencję → przy wysyłce czytamy
//  zawsze AKTUALNĄ konfigurację boiska. `captureShots` (opcjonalne, z main.js)
//  generuje 4 zrzuty PNG boiska (quote/courtShots.js) dołączane do payloadu jako
//  `screenshots[]`; jego błąd nie blokuje wysyłki (collectScreenshots → []).
// ============================================================================

// Endpoint: build prod → epufloor.app, dev-serwer → dev.epufloor.app,
// VITE_QUOTE_ENDPOINT nadpisuje oba. Logika i testy w quote/quoteEndpoint.js.
const ENDPOINT = resolveQuoteEndpoint(import.meta.env)

const TIMEOUT_MS = 15000

// Dane kontaktowe do specjalisty (widoczne w obu stanach modala).
const CONTACT = {
  phone: '+48 608 344 090',
  phoneHref: 'tel:+48608344090',
  email: 'katarzyna.kenig@epufloor.com',
}

const GDPR_TEXT =
  'Wyrażam zgodę na przetwarzanie moich danych osobowych przez Epufloor Sp. z o.o. ' +
  '(ul. Kopcińskiego 43, 90-143 Łódź, NIP 7272796330) w celu przygotowania kalkulacji ' +
  'boiska i kontaktu specjalisty w sprawie oferty. Podanie danych jest dobrowolne, ' +
  'a zgodę można w każdej chwili cofnąć.'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Budowa wiersza pola: label + input + miejsce na błąd inline (aria-describedby).
function buildField({ id, label, type, required, autocomplete }) {
  const wrap = document.createElement('div')
  wrap.className = 'qm-field'

  const lab = document.createElement('label')
  lab.className = 'qm-field__label'
  lab.htmlFor = id
  lab.textContent = required ? `${label} *` : label

  const input = document.createElement('input')
  input.id = id
  input.name = id
  input.type = type
  input.className = 'qm-field__input'
  if (autocomplete) input.autocomplete = autocomplete
  input.setAttribute('aria-describedby', `${id}-err`)

  const err = document.createElement('span')
  err.id = `${id}-err`
  err.className = 'qm-field__err'
  err.setAttribute('role', 'alert')

  wrap.append(lab, input, err)
  return { wrap, input, err }
}

// Stopka kontaktowa (telefon + e-mail jako klikalne linki). Tworzona dwa razy
// (jeden węzeł nie może żyć w dwóch sekcjach), więc fabryka.
function buildContact() {
  const box = document.createElement('div')
  box.className = 'qm-contact'
  box.innerHTML =
    'Masz pytania? Skontaktuj się już teraz: ' +
    `<a href="${CONTACT.phoneHref}">${CONTACT.phone}</a>, ` +
    `<a href="mailto:${CONTACT.email}">${CONTACT.email}</a>`
  return box
}

export function initQuoteModal({ state, catalog, hoopCatalog, onQuoteSent, captureShots }) {
  const trigger = document.getElementById('cta-quote')
  if (!trigger) return

  // --- Szkielet modala (backdrop + panel z sekcjami A i B) ---
  const backdrop = document.createElement('div')
  backdrop.className = 'qm'
  backdrop.hidden = true

  const panel = document.createElement('div')
  panel.className = 'qm__panel'
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-modal', 'true')
  panel.setAttribute('aria-labelledby', 'qm-title')

  const closeX = document.createElement('button')
  closeX.type = 'button'
  closeX.className = 'qm__close'
  closeX.setAttribute('aria-label', 'Zamknij')
  closeX.textContent = '×'

  // --- Stan A: formularz ---
  const formSec = document.createElement('div')
  formSec.className = 'qm__sec qm__sec--form'

  const title = document.createElement('h2')
  title.id = 'qm-title'
  title.className = 'qm__title'
  title.textContent = 'Zapytaj o wycenę'

  const intro = document.createElement('p')
  intro.className = 'qm__intro'
  intro.textContent =
    'Zostaw kontakt — przygotujemy wycenę boiska w skonfigurowanej wersji.'

  const form = document.createElement('form')
  form.className = 'qm-form'
  form.noValidate = true

  const fName = buildField({ id: 'qm-name', label: 'Imię i nazwisko', type: 'text', required: true, autocomplete: 'name' })
  const fLoc = buildField({ id: 'qm-location', label: 'Miejsce realizacji', type: 'text', required: true, autocomplete: 'address-level2' })
  const fPhone = buildField({ id: 'qm-phone', label: 'Telefon', type: 'tel', required: false, autocomplete: 'tel' })
  const fEmail = buildField({ id: 'qm-email', label: 'E-mail', type: 'email', required: true, autocomplete: 'email' })

  // Pole „Uwagi" — dodatkowe życzenia/wytyczne klienta; jedyne miejsce ich zbierania.
  // Treść trafia wprost do payloadu wyceny (textarea zachowuje wpis między otwarciami).
  const notesWrap = document.createElement('div')
  notesWrap.className = 'qm-field'
  const notesLab = document.createElement('label')
  notesLab.className = 'qm-field__label'
  notesLab.htmlFor = 'qm-notes'
  notesLab.textContent = 'Uwagi'
  const notesArea = document.createElement('textarea')
  notesArea.id = 'qm-notes'
  notesArea.name = 'qm-notes'
  notesArea.className = 'qm-field__textarea'
  notesArea.rows = 3
  notesArea.maxLength = 2000
  notesArea.placeholder = 'Dodatkowe życzenia, wytyczne, termin realizacji…'
  notesWrap.append(notesLab, notesArea)

  // Checkbox RODO + treść klauzuli.
  const gdprWrap = document.createElement('label')
  gdprWrap.className = 'qm-gdpr'
  const gdpr = document.createElement('input')
  gdpr.type = 'checkbox'
  gdpr.id = 'qm-gdpr'
  gdpr.className = 'qm-gdpr__check'
  gdpr.setAttribute('aria-describedby', 'qm-gdpr-err')
  const gdprText = document.createElement('span')
  gdprText.className = 'qm-gdpr__text'
  gdprText.textContent = GDPR_TEXT
  gdprWrap.append(gdpr, gdprText)
  const gdprErr = document.createElement('span')
  gdprErr.id = 'qm-gdpr-err'
  gdprErr.className = 'qm-field__err'
  gdprErr.setAttribute('role', 'alert')

  // Banner błędu wysyłki (nad przyciskiem).
  const banner = document.createElement('div')
  banner.className = 'qm-banner'
  banner.setAttribute('role', 'alert')
  banner.hidden = true

  const submit = document.createElement('button')
  submit.type = 'submit'
  submit.className = 'qm__submit'
  submit.textContent = 'Wyślij zapytanie'

  form.append(
    fName.wrap, fLoc.wrap, fPhone.wrap, fEmail.wrap, notesWrap,
    gdprWrap, gdprErr, banner, submit,
  )
  formSec.append(title, intro, form, buildContact())

  // --- Stan B: podziękowanie ---
  const thanksSec = document.createElement('div')
  thanksSec.className = 'qm__sec qm__sec--thanks'
  thanksSec.hidden = true
  const thanksMark = document.createElement('div')
  thanksMark.className = 'qm__check'
  thanksMark.textContent = '✓'
  const thanksTitle = document.createElement('h2')
  thanksTitle.className = 'qm__title'
  thanksTitle.textContent = 'Dziękujemy!'
  const thanksText = document.createElement('p')
  thanksText.className = 'qm__intro'
  thanksText.textContent =
    'Twoje zapytanie do nas dotarło. Specjalista niezwłocznie przygotuje wycenę boiska.'
  const thanksClose = document.createElement('button')
  thanksClose.type = 'button'
  thanksClose.className = 'qm__submit'
  thanksClose.textContent = 'Zamknij'
  thanksSec.append(thanksMark, thanksTitle, thanksText, buildContact(), thanksClose)

  panel.append(closeX, formSec, thanksSec)
  backdrop.append(panel)
  document.body.append(backdrop)

  // --- Walidacja inline ---
  const setError = (field, msg) => {
    field.input.setAttribute('aria-invalid', 'true')
    field.err.textContent = msg
  }
  const clearError = (field) => {
    field.input.removeAttribute('aria-invalid')
    field.err.textContent = ''
  }
  // Błąd znika przy edycji pola.
  ;[fName, fLoc, fPhone, fEmail].forEach((f) =>
    f.input.addEventListener('input', () => clearError(f)),
  )
  gdpr.addEventListener('change', () => {
    if (gdpr.checked) {
      gdpr.removeAttribute('aria-invalid')
      gdprErr.textContent = ''
    }
  })

  function validate() {
    let firstBad = null
    const fail = (field, msg) => {
      setError(field, msg)
      if (!firstBad) firstBad = field.input
    }
    if (!fName.input.value.trim()) fail(fName, 'Podaj imię i nazwisko.')
    if (!fLoc.input.value.trim()) fail(fLoc, 'Podaj miejsce realizacji.')
    const email = fEmail.input.value.trim()
    if (!email) fail(fEmail, 'Podaj adres e-mail.')
    else if (!EMAIL_RE.test(email)) fail(fEmail, 'Sprawdź format adresu e-mail.')
    if (!gdpr.checked) {
      gdpr.setAttribute('aria-invalid', 'true')
      gdprErr.textContent = 'Zaznacz zgodę, aby wysłać zapytanie.'
      if (!firstBad) firstBad = gdpr
    }
    if (firstBad) firstBad.focus()
    return !firstBad
  }

  // --- Wysyłka ---
  let sending = false
  function showBanner(msg) {
    banner.textContent = msg
    banner.hidden = false
  }
  function setSending(on) {
    sending = on
    submit.disabled = on
    submit.textContent = on ? 'Wysyłanie…' : 'Wyślij zapytanie'
  }

  async function send() {
    // Zrzuty boiska (4× PNG 16:9) generowane TERAZ — z aktualnej sceny, tuż
    // przed wysyłką; przycisk już pokazuje „Wysyłanie…".
    const screenshots = await collectScreenshots(captureShots)
    const payload = {
      name: fName.input.value.trim(),
      location: fLoc.input.value.trim(),
      phone: fPhone.input.value.trim(),
      email: fEmail.input.value.trim(),
      gdprConsent: gdpr.checked,
      configuration: buildQuoteConfiguration({
        state, catalog, hoopCatalog,
        notes: notesArea.value.trim(),
        viewUrl: buildViewUrl(state, location.origin),
      }),
      screenshots,
    }
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS)
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(payload),
        signal: ctrl.signal,
      })
      if (res.ok) {
        showState('thanks')
        onQuoteSent?.() // auto-zapis konfiguracji na listę (spina main.js)
        return
      }
      if (res.status === 429) showBanner('Wysłano już wiele zapytań z tego miejsca. Spróbuj ponownie za chwilę.')
      else if (res.status === 400) showBanner('Sprawdź poprawność danych i spróbuj ponownie.')
      else showBanner('Coś poszło nie tak po naszej stronie. Spróbuj ponownie za chwilę.')
    } catch {
      // AbortError (timeout) i błędy sieci trafiają tu tak samo.
      showBanner('Brak połączenia. Sprawdź internet i spróbuj ponownie.')
    } finally {
      clearTimeout(timer)
    }
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    if (sending) return
    banner.hidden = true
    if (!validate()) return
    setSending(true)
    await send()
    setSending(false)
  })

  // --- Przełączanie A↔B ---
  function showState(which) {
    const isForm = which === 'form'
    formSec.hidden = !isForm
    thanksSec.hidden = isForm
  }

  // --- Otwieranie / zamykanie + focus trap ---
  let lastFocus = null
  const focusables = () =>
    [...panel.querySelectorAll('button, [href], input, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((el) => !el.disabled && !el.hidden && el.offsetParent !== null)

  function open() {
    lastFocus = document.activeElement
    showState('form')
    banner.hidden = true
    backdrop.hidden = false
    fName.input.focus()
  }
  function close() {
    backdrop.hidden = true
    if (lastFocus && lastFocus.focus) lastFocus.focus()
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { close(); return }
    if (e.key !== 'Tab') return
    const els = focusables()
    if (els.length === 0) return
    const first = els[0]
    const last = els[els.length - 1]
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
  }

  trigger.addEventListener('click', open)
  closeX.addEventListener('click', close)
  thanksClose.addEventListener('click', close)
  backdrop.addEventListener('mousedown', (e) => { if (e.target === backdrop) close() })
  panel.addEventListener('keydown', onKeydown)
}
